import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { LLMProvider, ChatMessage, ChatSession } from '../types/llm'
import { MCPServer, MCPConnection, MCPServerStatus } from '../types/mcp'
import { v4 as uuidv4 } from 'uuid'

interface ChatStore {
  // LLM Configuration
  selectedProvider: LLMProvider | null
  selectedModel: string | null
  apiKeys: Record<LLMProvider, string>
  customProviderConfig: {
    baseUrl: string
    model: string
  }
  
  // Chat Sessions
  sessions: ChatSession[]
  currentSessionId: string | null
  
  // MCP Servers
  mcpServers: MCPServer[]
  mcpConnections: Record<string, MCPConnection>
  
  // Actions
  setProvider: (provider: LLMProvider) => void
  setModel: (model: string) => void
  setApiKey: (provider: LLMProvider, key: string) => void
  setCustomProviderConfig: (config: { baseUrl?: string; model?: string }) => void
  
  createSession: (provider: LLMProvider, model: string) => string
  selectSession: (sessionId: string) => void
  deleteSession: (sessionId: string) => void
  
  addMessage: (message: Omit<ChatMessage, 'id' | 'timestamp'>) => string
  updateMessage: (messageId: string, content: string) => void
  
  connectMCPServer: (serverId: string) => Promise<void>
  disconnectMCPServer: (serverId: string) => void
  
  initialize: () => void
  reset: () => void
}

export const useChatStore = create<ChatStore>()(
  persist(
    (set, get) => ({
      // Initial State
      selectedProvider: null,
      selectedModel: null,
      apiKeys: {
        [LLMProvider.OpenAI]: '',
        [LLMProvider.Anthropic]: '',
        [LLMProvider.Google]: '',
        [LLMProvider.Mistral]: '',
        [LLMProvider.Cohere]: '',
        [LLMProvider.Custom]: ''
      },
      customProviderConfig: {
        baseUrl: '',
        model: ''
      },
      sessions: [],
      currentSessionId: null,
      mcpServers: [],
      mcpConnections: {},

      // LLM Actions
      setProvider: (provider) => set({ selectedProvider: provider }),
      
      setModel: (model) => set({ selectedModel: model }),
      
      setApiKey: (provider, key) => 
        set((state) => ({
          apiKeys: { ...state.apiKeys, [provider]: key }
        })),

      setCustomProviderConfig: (config) =>
        set((state) => ({
          customProviderConfig: { ...state.customProviderConfig, ...config }
        })),

      // Session Actions
      createSession: (provider, model) => {
        const sessionId = uuidv4()
        const newSession: ChatSession = {
          id: sessionId,
          title: 'New Chat',
          messages: [],
          provider,
          model,
          createdAt: new Date(),
          updatedAt: new Date()
        }
        
        set((state) => ({
          sessions: [...state.sessions, newSession],
          currentSessionId: sessionId
        }))
        
        return sessionId
      },

      selectSession: (sessionId) => set({ currentSessionId: sessionId }),

      deleteSession: (sessionId) => 
        set((state) => ({
          sessions: state.sessions.filter(s => s.id !== sessionId),
          currentSessionId: state.currentSessionId === sessionId 
            ? state.sessions[0]?.id || null 
            : state.currentSessionId
        })),

      // Message Actions
      addMessage: (message) => {
        const { currentSessionId } = get()
        if (!currentSessionId) return ''

        const newMessage: ChatMessage = {
          ...message,
          id: uuidv4(),
          timestamp: new Date()
        }

        set((state) => ({
          sessions: state.sessions.map(session => 
            session.id === currentSessionId
              ? {
                  ...session,
                  messages: [...session.messages, newMessage],
                  updatedAt: new Date(),
                  title: session.messages.length === 0 && message.role === 'user' 
                    ? message.content.slice(0, 50) + (message.content.length > 50 ? '...' : '')
                    : session.title
                }
              : session
          )
        }))
        
        return newMessage.id
      },

      updateMessage: (messageId, content) => {
        set((state) => ({
          sessions: state.sessions.map(session => ({
            ...session,
            messages: session.messages.map(msg => 
              msg.id === messageId ? { ...msg, content } : msg
            )
          }))
        }))
      },

      // MCP Server Actions
      connectMCPServer: async (serverId) => {
        const server = get().mcpServers.find(s => s.id === serverId)
        if (!server) return

        set((state) => ({
          mcpConnections: {
            ...state.mcpConnections,
            [serverId]: {
              serverId,
              sessionId: uuidv4(),
              status: MCPServerStatus.Connecting,
              tools: [],
              resources: [],
              prompts: []
            }
          }
        }))

        // TODO: Implement actual MCP connection logic
        // For now, simulate connection
        setTimeout(() => {
          set((state) => ({
            mcpConnections: {
              ...state.mcpConnections,
              [serverId]: {
                ...state.mcpConnections[serverId],
                status: MCPServerStatus.Connected
              }
            }
          }))
        }, 1000)
      },

      disconnectMCPServer: (serverId) => {
        set((state) => {
          const { [serverId]: _, ...rest } = state.mcpConnections
          return { mcpConnections: rest }
        })
      },

      // Initialization
      initialize: () => {
        const state = get()
        
        // Load default MCP servers
        if (state.mcpServers.length === 0) {
          set({
            mcpServers: [
              {
                id: 'cloudflare-bindings',
                name: 'Cloudflare Bindings',
                description: 'Access D1, R2, and KV storage',
                url: 'https://mcp-bindings.example.workers.dev/sse',
                requiresAuth: true,
                capabilities: [],
                status: MCPServerStatus.Disconnected
              },
              {
                id: 'cloudflare-logs',
                name: 'Cloudflare Logs',
                description: 'Query worker logs and analytics',
                url: 'https://mcp-logs.example.workers.dev/sse',
                requiresAuth: true,
                capabilities: [],
                status: MCPServerStatus.Disconnected
              }
            ]
          })
        }

        // Create initial session if none exists
        if (state.sessions.length === 0 && state.selectedProvider && state.selectedModel) {
          const sessionId = get().createSession(state.selectedProvider, state.selectedModel)
          set({ currentSessionId: sessionId })
        }
      },

      reset: () => {
        set({
          selectedProvider: null,
          selectedModel: null,
          apiKeys: {
            [LLMProvider.OpenAI]: '',
            [LLMProvider.Anthropic]: '',
            [LLMProvider.Google]: '',
            [LLMProvider.Mistral]: '',
            [LLMProvider.Cohere]: '',
            [LLMProvider.Custom]: ''
          },
          sessions: [],
          currentSessionId: null,
          mcpConnections: {}
        })
      }
    }),
    {
      name: 'aniify-chat-store',
      partialize: (state) => ({
        selectedProvider: state.selectedProvider,
        selectedModel: state.selectedModel,
        apiKeys: state.apiKeys,
        customProviderConfig: state.customProviderConfig,
        sessions: state.sessions,
        currentSessionId: state.currentSessionId,
        mcpServers: state.mcpServers
      })
    }
  )
)
